import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Grid,
  MenuItem,
  Box,
  InputLabel,
  FormControl,
  Select,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { 
  Save, 
  ArrowBack, 
  PhotoCamera, 
  Cameraswitch, 
  Visibility, 
  Delete as DeleteIcon, 
  ZoomIn, 
  ZoomOut,
  Close, 
  RotateLeft, 
  RotateRight, 
  Download, 
  ArrowForwardIos, 
  ArrowBackIosNew, 
  Image as ImageIcon,
  CameraAlt as CameraAltIcon
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../../../firebase.config';
import { useAuth } from '../../../AuthContext';
import { useUserContext } from '../../../components/context/UserContext';
import { compressAndResizeImage } from '../../../utils/imageUtils';

const expenseTypes = [
  'Education',
  'Entertainment',
  'Event',
  'Gift',
  'Internet/Broadband',
  'Meal',
  'Medical',
  'Office Supplies',
  'Other',
  'Rent',
  'Shopping',
  'Subscription',
  'Transportation',
  'Travel',
  'Utilities'
];

const CardForm = () => {
  const voucherCardRef = useRef(null);
  const { userProfile } = useUserContext();
  // Add state for input mode: 'photo' or 'voucher'
  const [inputMode, setInputMode] = useState('photo');
  // Generate unique voucher ID: VOUCHER-[timestamp]-[random]
  const getNextVoucherId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `VOUCHER-${timestamp}-${random}`;
  };

  // Voucher images state for each voucher
  const [voucherImages, setVoucherImages] = useState([]);

  // Handle voucher image upload (multiple)
  const handleVoucherImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const filesWithPreview = files.map(file => ({
      file,
      id: URL.createObjectURL(file),
      preview: URL.createObjectURL(file)
    }));
    setVoucherImages(prev => [...prev, ...filesWithPreview]);
  };

  // Remove image from voucherImages
  const removeVoucherImage = (id) => {
    setVoucherImages(prev => prev.filter(img => img.id !== id));
  };

  const handleAddVoucher = () => {
    if (!formData.businessName || !formData.amount) {
      setVoucherError('Title and Amount are required');
      return;
    }
    setVoucherError('');
    // Add voucher first, then render to image
    const newVoucher = {
      id: getNextVoucherId(),
      title: formData.businessName,
      description: formData.additionalDetails,
      amount: formData.amount,
      type: 'voucher',
      images: voucherImages,
    };
    setVouchers((prev) => [...prev, newVoucher]);
    setVoucherImages([]);
    setTimeout(async () => {
      if (voucherCardRef.current) {
        const canvas = await html2canvas(voucherCardRef.current);
        const dataUrl = canvas.toDataURL('image/png');
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const storageRef = ref(storage, `vouchers/${newVoucher.id}.png`);
        await uploadBytes(storageRef, blob);
        const imageUrl = await getDownloadURL(storageRef);
        setVouchers((prev) => prev.map(v => v.id === newVoucher.id ? { ...v, imageUrl } : v));
      }
    }, 500);
  };

  const handleRemoveVoucher = (id) => {
    setVouchers((prev) => prev.filter((v) => v.id !== id));
  };
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const webcamRef = useRef(null);
  const [images, setImages] = useState([]); // Local files to upload
  const [imageUrls, setImageUrls] = useState([]); // URLs from Firestore
  const [allImages, setAllImages] = useState([]); // Combined array of local and remote images
  // Voucher states
  const [vouchers, setVouchers] = useState([]);
  const [voucherError, setVoucherError] = useState('');
  const [entryError, setEntryError] = useState('');
  const [formData, setFormData] = useState({ 
    expenseType: '',
    date: new Date().toISOString().split('T')[0],
    claimBy: '',
    businessName: '',
    amount: '',
    currency: 'INR',
    additionalDetails: '',
    status: 'Raised', // Default status for all users
    imageUrls: [],
    // GST fields (unchangeable by user)
    cgst: '',
    sgst: '',
    igst: '',
    totalWithGST: '',
    // Metadata fields - initialize with empty values
    createdBy: currentUser?.email || '', // Will be set to current user when creating
    createdOn: new Date().toISOString(),
    updatedBy: '', // Empty initially, will be set on update
    updatedOn: '' // Empty initially, will be set on update
  });

  // GST calculation logic (example: 9% CGST, 9% SGST, 0% IGST)
  const GST_RATES = { cgst: 9, sgst: 9, igst: 0 };
  const calcTotalWithGST = (amount) => {
    const base = parseFloat(amount) || 0;
    const cgst = GST_RATES.cgst;
    const sgst = GST_RATES.sgst;
    const igst = GST_RATES.igst;
    const total = base + (base * (cgst + sgst + igst) / 100);
    return {
      cgst: base ? (base * cgst / 100).toFixed(2) : '',
      sgst: base ? (base * sgst / 100).toFixed(2) : '',
      igst: base ? (base * igst / 100).toFixed(2) : '',
      totalWithGST: base ? total.toFixed(2) : ''
    };
  };

  // Update GST fields when amount changes
  useEffect(() => {
    const { cgst, sgst, igst, totalWithGST } = calcTotalWithGST(formData.amount);
    setFormData(prev => ({
      ...prev,
      cgst,
      sgst,
      igst,
      totalWithGST
    }));
    // eslint-disable-next-line
  }, [formData.amount]);
              {/* GST Fields (unchangeable, auto-calculated) */}
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    name="cgst"
                    label="CGST (₹)"
                    value={formData.cgst}
                    margin="normal"
                    InputProps={{ readOnly: true }}
                    helperText="Auto-calculated (9%)"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    name="sgst"
                    label="SGST (₹)"
                    value={formData.sgst}
                    margin="normal"
                    InputProps={{ readOnly: true }}
                    helperText="Auto-calculated (9%)"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    name="igst"
                    label="IGST (₹)"
                    value={formData.igst}
                    margin="normal"
                    InputProps={{ readOnly: true }}
                    helperText="Auto-calculated (0%)"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Total Amount (₹)"
                    value={formData.totalWithGST}
                    margin="normal"
                    InputProps={{ readOnly: true }}
                    helperText="Auto-calculated with GST"
                  />
                </Grid>
              </>
  const statusOptions = [
    { value: 'Raised', label: 'Raised' },
    { value: 'Verified', label: 'Verified' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' }
  ];
  
  const [charCount, setCharCount] = useState(0);
  
  const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' }
  ];
  
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return currency ? currency.symbol : '₹';
  };

  const handleChange = (e) => {
  // Voucher form change

  // Generate next voucher ID
  const getNextVoucherId = () => {
    const prefix = 'VCHR-';
    const num = vouchers.length + 1;
    return `${prefix}${String(num).padStart(3, '0')}`;
  };

  // Add voucher
  const handleAddVoucher = () => {
    if (!formData.businessName || !formData.amount) {
      setVoucherError('Title and Amount are required');
      return;
    }
    setVoucherError('');
    setVouchers((prev) => [
      ...prev,
      {
        id: getNextVoucherId(),
        title: formData.businessName,
        description: formData.additionalDetails,
        amount: formData.amount,
        type: 'voucher',
      },
    ]);
  };

  // Remove voucher
  const handleRemoveVoucher = (id) => {
    setVouchers((prev) => prev.filter((v) => v.id !== id));
  };
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value 
    }));
    if (name === 'additionalDetails') {
      setCharCount(value.length);
    }
  };
  
  const handleAmountChange = (e) => {
    const { value } = e.target;
    // Only allow numbers and one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        amount: value
      }));
    }
  };
  
  const incrementAmount = () => {
    const currentAmount = parseFloat(formData.amount) || 0;
    setFormData(prev => ({
      ...prev,
      amount: (currentAmount + 1).toString()
    }));
  };
  
  const decrementAmount = () => {
    const currentAmount = parseFloat(formData.amount) || 0;
    if (currentAmount > 0) {
      setFormData(prev => ({
        ...prev,
        amount: (currentAmount - 1).toString()
      }));
    }
  };

  useEffect(() => {
    const fetchExpense = async () => {
      if (id) {
        try {
          setLoading(true);
          const docRef = doc(firestore, 'expense', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const urls = data.imageUrls || [];
            // Set form data with existing values
            setFormData({ 
              ...data,
              // Keep the original creator and creation date
              createdBy: data.createdBy || currentUser?.email || '',
              createdOn: data.createdOn || new Date().toISOString(),
              // Only show updatedBy/updatedOn if they exist
              updatedBy: data.updatedBy || '',
              updatedOn: data.updatedOn || '',
              imageUrls: urls 
            });
            setImageUrls(urls);
            setIsEditMode(true);
            
            // Set preview image if there are any images
            if (urls.length > 0) {
              setPreviewImage(urls[0]);
            }
          }
        } catch (error) {
          console.error('Error fetching expense: ', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchExpense();
  }, [id]);
  
  // Update allImages whenever images or imageUrls change
  useEffect(() => {
    setAllImages([...images, ...imageUrls]);
  }, [images, imageUrls]);

  useEffect(() => {
    return () => {
      images.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
        if (typeof image.id === 'string' && image.id.startsWith('blob:')) {
          URL.revokeObjectURL(image.id);
        }
      });
    };
  }, [images]);

  // Image handling functions
  const removeImage = (id) => {
    setImages(prev => {
      const newImages = prev.filter(img => {
        if (img.id === id) {
          if (img.preview) URL.revokeObjectURL(img.preview);
          if (typeof img.id === 'string' && img.id.startsWith('blob:')) URL.revokeObjectURL(img.id);
          return false;
        }
        return true;
      });
      return newImages;
    });
  };

  const handlePreviewImage = (img, index = 0) => {
    if (!img) return;
    
    try {
      let imageUrl;
      if (img instanceof File) {
        // imageUrl = URL.createObjectURL(img);
      } else if (img.file) {
        imageUrl = URL.createObjectURL(img.file);
      } else {
        imageUrl = img.url || img;
      }
      
      setPreviewImage(imageUrl);
      setCurrentImageIndex(index);
      setZoomLevel(1);
      setRotation(0);
      setIsPreviewOpen(true);
    } catch (error) {
      console.error('Error creating image preview:', error);
    }
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setZoomLevel(1);
    setRotation(0);
    // Revoke the object URL to prevent memory leaks
    if (typeof previewImage === 'string' && previewImage.startsWith('blob:')) {
      URL.revokeObjectURL(previewImage);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense-receipt-${new Date().getTime()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const handleNextImage = () => {
    const allImages = [...images, ...imageUrls];
    if (allImages.length > 1) {
      const nextIndex = (currentImageIndex + 1) % allImages.length;
      handlePreviewImage(allImages[nextIndex], nextIndex);
    }
  };

  const handlePrevImage = () => {
    const allImages = [...images, ...imageUrls];
    if (allImages.length > 1) {
      const prevIndex = currentImageIndex === 0 ? allImages.length - 1 : currentImageIndex - 1;
      handlePreviewImage(allImages[prevIndex], prevIndex);
    }
  };

  const openCamera = () => {
    setIsCameraOpen(true);
  };

  // Helper function to convert data URI to blob
  const dataURItoBlob = (dataURI) => {
    if (!dataURI) return null;
    try {
      const byteString = atob(dataURI.split(',')[1]);
      const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    } catch (error) {
      console.error('Error converting data URI to Blob:', error);
      return null;
    }
  };

  const capturePhoto = async () => {
    if (webcamRef.current) {
      try {
        const imageSrc = webcamRef.current.getScreenshot();
        // Close the camera dialog after capturing
        setIsCameraOpen(false);
        if (imageSrc) {
          const blob = dataURItoBlob(imageSrc);
          if (blob) {
            await compressImage(blob, 1000, true);
            // setIsCameraOpen(false);
          }
        }
      } catch (error) {
        console.error('Error capturing photo:', error);
      }
    }
  };
  // Compress image function to target 25KB-50KB size
  const compressImage = (file, maxWidth, fromCamera = false) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          // First, resize to max width if needed
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          
          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;
          
          // Draw image on canvas
          ctx.drawImage(img, 0, 0, width, height);
          
          // Function to compress to target size
          const compressToTargetSize = async (quality = 0.9, minQuality = 0.1) => {
            return new Promise((resolveBlob) => {
              canvas.toBlob(
                (blob) => {
                  // If we're below 25KB, increase quality if possible
                  if (blob.size < 25 * 1024 && quality < 0.95) {
                    resolveBlob(compressToTargetSize(Math.min(quality + 0.1, 0.95), minQuality));
                  } 
                  // If we're above 50KB, reduce quality
                  else if (blob.size > 50 * 1024 && quality > minQuality) {
                    resolveBlob(compressToTargetSize(quality * 0.8, minQuality));
                  } 
                  // If we're in the target range or at minimum quality, accept the result
                  else {
                    resolveBlob(blob);
                  }
                },
                'image/jpeg',
                quality
              );
            });
          };
          
          try {
            // Start with 90% quality
            const compressedBlob = await compressToTargetSize(0.9, 0.1);
            
            // If still too large, reduce dimensions
            if (compressedBlob.size > 50 * 1024) {
              // Reduce dimensions by 20% and try again
              const reductionFactor = 0.8;
              canvas.width = Math.max(100, width * reductionFactor);
              canvas.height = Math.max(100, height * reductionFactor);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              const finalBlob = await compressToTargetSize(0.9, 0.1);
              createAndHandleFile(finalBlob);
            } else {
              createAndHandleFile(compressedBlob);
            }
          } catch (error) {
            console.error('Error during compression:', error);
            // Fallback to original compression if there's an error
            canvas.toBlob(
              (blob) => createAndHandleFile(blob),
              'image/jpeg',
              0.7
            );
          }
          
          function createAndHandleFile(blob) {
            const timestamp = new Date().getTime();
            const newFile = new File([blob], `card-image-${timestamp}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            if (fromCamera) {
              setImages(prev => [...prev, { file: newFile, id: timestamp }]);
            } else {
              resolve(newFile);
            }
          }
        };
      };
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - images.length);
    if (files.length === 0) {
      alert('You can upload up to 3 images in total');
      return;
    }
    
    try {
      // Process each file to compress and resize
      const processedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            // Only process image files
            if (file.type.startsWith('image/')) {
              const compressedFile = await compressAndResizeImage(file, 25);
              return {
                file: compressedFile,
                id: URL.createObjectURL(compressedFile),
                preview: URL.createObjectURL(compressedFile)
              };
            }
            // For non-image files, return as is
            return {
              file,
              id: URL.createObjectURL(file),
              preview: URL.createObjectURL(file)
            };
          } catch (error) {
            console.error('Error processing image:', error);
            // If there's an error, return the original file
            return {
              file,
              id: URL.createObjectURL(file),
              preview: URL.createObjectURL(file)
            };
          }
        })
      );
      
      setImages(prev => [...prev, ...processedFiles]);
    } catch (error) {
      console.error('Error processing images:', error);
      alert('Error processing images. Please try again.');
    }
  };

 

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Basic validation
    if (!formData.expenseType || !formData.businessName || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }
    // Must have at least one entry (image or voucher)
    if (images.length + imageUrls.length === 0 && vouchers.length === 0) {
      setEntryError('Please provide at least one image or voucher.');
      return;
    } else {
      setEntryError('');
    }
    setLoading(true);
    try {
      const amountValue = parseFloat(formData.amount);
      if (isNaN(amountValue)) {
        throw new Error('Invalid amount');
      }
      // Separate files and URLs
      let uploadedImageUrls = [...imageUrls]; // URLs added by user
      for (const img of images) {
        // Fix: use img.file if available, otherwise fallback to img if it's a File
        const fileToUpload = img.file ? img.file : (img instanceof File ? img : null);
        if (fileToUpload) {
          const storageRef = ref(storage, `images/${currentUser.uid}/${Date.now()}_${fileToUpload.name}`);
          await uploadBytes(storageRef, fileToUpload);
          const url = await getDownloadURL(storageRef);
          uploadedImageUrls.push(url);
        }
      }
      // Prepare vouchers for saving
      const voucherDocs = vouchers.map((v) => ({ ...v }));
      const tenantId = sessionStorage.getItem('tenantId');  // Get tenantId from sessionStorage
      const expenseData = {
        ...formData,  // Include all form fields
        amount: amountValue,
        imageUrls: uploadedImageUrls,
        vouchers: voucherDocs,
        status: formData.status || 'Raised',
        uid: currentUser?.uid || '',
        email: currentUser?.email || '',
        date: formData.date || new Date().toISOString().split('T')[0],
        // User tracking
        createdBy: isEditMode ? (formData.createdBy || currentUser?.email || '') : (currentUser?.email || ''),
        updatedBy: currentUser?.email || '',
        updatedOn: serverTimestamp(),
        tenantId: userProfile.tenantId
      };

      // Save the main expense document
      if (isEditMode) { 
        await updateDoc(doc(firestore, 'expense', id), expenseData);
      } else {
        await addDoc(collection(firestore, 'expense'), expenseData);
      }

      // Handle vouchers
      if (voucherDocs.length > 0) {
        expenseData.vouchers = [];
        for (const v of voucherDocs) {
          const voucherData = {
            ...v,
            expenseId: isEditMode ? id : '',  // Link to main expense if in edit mode
            status: formData.status || 'Raised',
            createdAt: serverTimestamp(),
            createdBy: currentUser?.email || '',
            uid: currentUser?.uid || '',
            tenantId: userProfile.tenantId,
            type: 'voucher'
          };
          expenseData.vouchers.push(voucherData);
        }
      }

      // Reset form state
      setImages([]);
      setImageUrls([]);
      setVouchers([]);
      
      // Navigate to the list page
      navigate('/admin/items');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} document: `, error);
      alert(`Failed to ${isEditMode ? 'update' : 'save'} expense. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {isCameraOpen && (
        <Dialog open={isCameraOpen} onClose={() => setIsCameraOpen(false)} maxWidth="sm" fullWidth>
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Take a photo of the visiting card</Typography>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode }}
              style={{ width: '100%', maxHeight: '500px', margin: '0 auto' }}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={capturePhoto}
                startIcon={<PhotoCamera />}
              >
                Capture
              </Button>
              {/* <Button 
                variant="outlined" 
                onClick={() => setFacingMode(facingMode === "user" ? "environment" : "user")}
                startIcon={<Cameraswitch />}
              >
                Switch Camera
              </Button> */}
              <Button 
                variant="outlined" 
                color="error" 
                onClick={() => setIsCameraOpen(false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Enhanced Image Preview Dialog */}
      <Dialog 
        open={isPreviewOpen} 
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'auto',
            height: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          backgroundColor: 'primary.main',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Visibility sx={{ mr: 1 }} />
            <span>photo Preview</span>
          </Box>
          <IconButton 
            onClick={handleClosePreview}
            size="small"
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <Typography variant="subtitle2" sx={{ ml: 1 }}>
              photo {currentImageIndex + 1} of {images.length + imageUrls.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={handleRotateLeft}
                size="small"
                title="Rotate left"
              >
                <RotateLeft />
              </IconButton>
              <IconButton 
                onClick={handleRotateRight}
                size="small"
                title="Rotate right"
              >
                <RotateRight />
              </IconButton>
              <IconButton 
                onClick={handleZoomOut}
                size="small"
                disabled={zoomLevel <= 0.5}
                title="Zoom out"
              >
                <ZoomOut />
              </IconButton>
              <IconButton 
                onClick={handleZoomIn}
                size="small"
                disabled={zoomLevel >= 3}
                title="Zoom in"
              >
                <ZoomIn />
              </IconButton>
              {/* <IconButton 
                onClick={() => handleDownload(previewImage)}
                size="small"
                title="Download image"
              >
                <Download />
              </IconButton> */}
            </Box>
          </Box>
          <Box sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            backgroundColor: 'rgba(0,0,0,0.05)',
            minHeight: '300px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {previewImage ? (
              <Box
                sx={{
                  transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease',
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: 2
                }}
              >
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{ 
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'block'
                  }}
                  onError={(e) => {
                    console.error('Error loading image:', previewImage);
                    e.target.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                color: 'text.secondary',
                p: 3
              }}>
                <ImageIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body1">No photo selected</Typography>
              </Box>
            )}
            {(images.length + imageUrls.length) > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  size="large"
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <ArrowBackIosNew />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  size="large"
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1">
            Add New Expense
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Tenant ID Field (read-only, hardcoded for now) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tenant ID"
                value={userProfile?.tenantId || 'Loading...'}
                InputProps={{ readOnly: true }}
                margin="normal"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="expense-type-label">Expense Type</InputLabel>
                <Select
                  labelId="expense-type-label"
                  name="expenseType"
                  value={formData.expenseType || ''}
                  onChange={handleChange}
                  label="Expense Type *"
                  required
                >
                  {expenseTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expense Date"
                type="datetime-local"
                name="date"
                value={formData.date}
                onChange={handleChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="claimBy"
                label="Claimed By"
                value={formData.claimBy || ''}
                onChange={handleChange}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="businessName"
                label="Invoice By"
                value={formData.businessName || ''}
                onChange={handleChange}
                margin="normal" 
                required
              />
            </Grid>
            
            {currentUser?.role === 'admin' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status || 'Raised'}
                    onChange={handleChange}
                    label="Status"
                    required
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {currentUser?.role !== 'admin' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <TextField
                    label="Status"
                    value={formData.status || 'Raised'}
                    InputProps={{
                      readOnly: true,
                    }}
                    // margin="normal"
                  />
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ width: '130px', mr: 2, mt: 2 }}>
                  <FormControl fullWidth margin="none" required>
                    <InputLabel id="currency-label">Currency</InputLabel>
                    <Select
                      labelId="currency-label"
                      name="currency"
                      value={formData.currency || 'INR'}
                      onChange={handleChange}
                      label="Currency"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '56px',
                          '& fieldset': {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0
                          }
                        }
                      }}
                    >
                      {currencies.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {`${currency.code} (${currency.symbol})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>  
                </Box>
                <Box sx={{ flex: 1, position: 'relative', mt: 2 }}>
                  <TextField
                    fullWidth
                    label="  Expense"
                    name="amount"
                    value={formData.amount}
                    onChange={handleAmountChange}
                    margin="none"
                    sx={{
                      pr: '100px',
                      '& .MuiOutlinedInput-root': {
                        height: '56px',
                        '& fieldset': {
                         
                        }
                      }
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '^\\.?\\d+\\.?\\d*$',
                      title: 'Please enter a valid number',
                      style: { 
                        paddingLeft: '40px',
                        paddingRight: '60px',
                        height: '100%',
                        boxSizing: 'border-box'
                      } 
                    }}
                    
                    required
                  />
                  {/* Currency symbol removed from input as it's shown in the dropdown */}
                  <Box sx={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                    fontWeight: 'bold',
                    display: 'none', // Hide the currency symbol
                    pointerEvents: 'none'
                  }}>
                    {currencies.find(c => c.code === formData.currency)?.symbol || '₹'}
                  </Box>
                  <Box sx={{ 
                    position: 'absolute', 
                    right: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                  }}>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={incrementAmount}
                      sx={{ 
                        minWidth: '24px', 
                        height: '20px', 
                        p: 0, 
                        lineHeight: 1,
                        fontSize: '0.7rem'
                      }}
                    >
                      ▲
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={decrementAmount}
                      disabled={!formData.amount || parseFloat(formData.amount) <= 0}
                      sx={{ 
                        minWidth: '24px', 
                        height: '20px', 
                        p: 0, 
                        lineHeight: 1,
                        fontSize: '0.7rem'
                      }}
                    >
                      ▼
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box>
                {/* GST Box under Expense */}
            <Grid item xs={12}>
              <Box sx={{
                display: 'flex',
                gap: 2,
                mt: 1,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                background: '#f7f7fa',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap'
              }}>
                <TextField
                  label="CGST (₹)"
                  value={formData.cgst}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  helperText="Auto (9%)"
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  label="SGST (₹)"
                  value={formData.sgst}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  helperText="Auto (9%)"
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  label="IGST (₹)"
                  value={formData.igst}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  helperText="Auto (0%)"
                  sx={{ flex: 1, minWidth: 120 }}
                />
                <TextField
                  label="Total (₹ incl. GST)"
                  value={formData.totalWithGST}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  helperText="Auto with GST"
                  sx={{ flex: 1, minWidth: 140 }}
                />
              </Box>
            </Grid>
                <TextField
                  fullWidth
                  label="Additional Details"
                  name="additionalDetails"
                  value={formData.additionalDetails}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 500 }}
                />
                <Typography 
                  variant="caption" 
                  color={charCount === 500 ? 'error' : 'text.secondary'}
                  sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                >
                  {charCount}/500 characters
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                  <PhotoCamera sx={{ mr: 1 }} />
                  Invoice and Quotation photos / Vouchers
                </Typography>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mb: 2 }}>
                  You can upload up to 3 images ({3 - images.length} remaining) or add vouchers below.
                </Typography>
                {/* Error for entry validation */}
                {entryError && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>{entryError}</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  {/* Preview newly selected images */}
                  {images.map((img, idx) => {
                    const src = img.preview || 
                              (img.file ? URL.createObjectURL(img.file) : 
                              (img instanceof File ? URL.createObjectURL(img) : 
                              (img.url || img)));
                    return (
                      <Box key={img.id || idx} sx={{ position: 'relative' }}>
                        <Box
                          component="img"
                          src={src}
                          alt={`Preview ${idx + 1}`}
                          sx={{ 
                            width: 100, 
                            height: 70, 
                            objectFit: 'cover',   
                            borderRadius: 1, 
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              boxShadow: 2
                            }
                          }}
                          onClick={() => handlePreviewImage(img, idx)}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(img.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: 'error.main',
                            color: 'white',
                            width: 24,
                            height: 24,
                            '&:hover': {
                              backgroundColor: 'error.dark',
                            },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                  {imageUrls && imageUrls.map((url, idx) => (
                    <Box key={`url-${idx}`} sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={url}
                        alt={`URL Preview ${idx + 1}`}
                        sx={{ 
                          width: 100, 
                          height: 70, 
                          objectFit: 'cover', 
                          borderRadius: 4, 
                          border: '1px solid #ccc',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 2
                          }
                        }}
                        onClick={() => handlePreviewImage(url, images.length + idx)}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageUrls(prev => prev.filter((_, i) => i !== idx));
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'error.main',
                          color: 'white',
                          width: 24,
                          height: 24,
                          '&:hover': {
                            backgroundColor: 'error.dark',
                          },
                        }}
                      > 
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {/* Vouchers display - clean, organized, supports images and delete by ID */}
                  {vouchers.map((voucher, idx) => {
                    // Format date and time for display
                    const formattedDate = new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    });
                    return (
                      <Box key={voucher.id} ref={idx === vouchers.length - 1 ? voucherCardRef : null} sx={{ position: 'relative', minWidth: 220, maxWidth: 320, p: 2, border: '1px solid #bdbdbd', borderRadius: 2, backgroundColor: '#fff', boxShadow: 2, display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        {/* subtle top-left close button (very low visibility) */}
                        <IconButton
                          onClick={() => handleRemoveVoucher(voucher.id)}
                          size="small"
                          aria-label="remove voucher"
                          sx={{
                            position: 'absolute',
                            top: 0.5,
                            right: 4,
                            zIndex: 1,
                            width: 10,
                            height: 20,
                            p: 0.25,
                            color: 'red', // Changed to standard red
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              '& .MuiSvgIcon-root': {
                                color: 'darkred' // Darker red on hover
                              }
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '1rem',
                              color: 'red' // Ensures the icon is red
                            }
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{voucher.id}</Typography>
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>{voucher.title || voucher.businessName}</Typography>
                        <Typography variant="body2" color="text.secondary">{voucher.description}</Typography>
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>Amount: {voucher.amount}</Typography>
                        {/* Voucher images */}
                        {voucher.images && voucher.images.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {voucher.images.map((img, i) => (
                              <Box key={img.id || i} sx={{ position: 'relative' }}>
                                <Box
                                  component="img"
                                  src={img.preview || (img.file ? URL.createObjectURL(img.file) : img.id)}
                                  alt={`Voucher Img ${i + 1}`}
                                  sx={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                                />
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {images.length + imageUrls.length + vouchers.length < 3 && (
                    null
                  )}
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', mb: 1 }}>
                    Add photos or Voucher:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl component="fieldset">
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <label>
                          <input
                            type="radio"
                            value="photo"
                            checked={inputMode === 'photo'}
                            onChange={() => setInputMode('photo')}
                          /> Photo
                        </label>
                        <label>
                          <input
                            type="radio"
                            value="voucher"
                            checked={inputMode === 'voucher'}
                            onChange={() => setInputMode('voucher')}
                          /> Voucher
                        </label>
                      </Box>
                    </FormControl>
                  </Box>
                  {/* Photo input section */}
                  {inputMode === 'photo' && (
                    <>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CameraAltIcon />}
                          disabled={images.length >= 3}
                          sx={{
                            flex: 1,
                            minWidth: '120px',
                            py: 1.5,
                            borderRadius: 2,
                            borderStyle: 'dashed',
                            '&:hover': {
                              borderStyle: 'solid',
                              backgroundColor: 'action.hover'
                            }
                          }}
                          onClick={() => setIsCameraOpen(true)}
                        >
                          Take Photo
                        </Button>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<PhotoCamera />}
                          disabled={images.length >= 3}
                          sx={{
                            flex: 1,
                            minWidth: '120px',
                            py: 1.5,
                            borderRadius: 2,
                            borderStyle: 'dashed',
                            '&:hover': {
                              borderStyle: 'solid',
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          Upload
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                          />
                        </Button>
                      </Box>
                      {images.length < 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {3 - images.length} more photo{images.length < 2 ? 's' : ''} can be added
                        </Typography>
                      )}
                    </>
                  )}
                  {/* Voucher input section */}
                  {inputMode === 'voucher' && (
                    <Box sx={{ mt: 2, p: 2, border: '1px dashed #bdbdbd', borderRadius: 2, backgroundColor: '#fafafa', maxWidth: 350 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>Create Voucher from Expense Info</Typography>
                      {voucherError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{voucherError}</Typography>}
                      <Button variant="contained" color="primary" onClick={handleAddVoucher} sx={{ mt: 1 }}>Add Voucher</Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Expense' : 'Save Expense'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      {/* Webcam Dialog */}
      <Dialog 
        open={isCameraOpen} 
        onClose={() => setIsCameraOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5
        }}>
          <CameraAltIcon />
          Take a Photo
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          py: 3,
          bgcolor: '#f5f5f5'
        }}>
          <Box sx={{ 
            width: '100%', 
            maxWidth: 600, 
            height: 400,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 3,
            position: 'relative'
          }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode: 'environment',
                width: 1280,
                height: 720 
              }}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
          <Box sx={{ 
            mt: 3, 
            display: 'flex', 
            gap: 2,
            width: '100%',
            maxWidth: 600,
            justifyContent: 'center'
          }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={capturePhoto}
              startIcon={<CameraAltIcon />}
              size="large"
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              Capture
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => setIsCameraOpen(false)}
              size="large"
              sx={{
                px: 4,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default CardForm;
